/**
 * Typed client for a site's UCP checkout readiness (ADR-0215):
 *
 *   GET /v1/sites/:site_id/ucp — what the public profile advertises and,
 *       when checkout is not advertised, the closed-vocabulary reasons.
 *   PUT /v1/sites/:site_id/ucp — the merchant-supplied Google Pay fields
 *       (merchant id is required for PRODUCTION; TEST needs none).
 *       Also shipping & tax, and which of the merchant's Stripe promotion
 *       codes agents may use (ADR-0254: off | all | allow-list).
 *   POST|DELETE /v1/sites/:site_id/ucp/platform-key — mint / rotate /
 *       revoke the access key Google's checkout presents (ADR-0215 §11).
 *       The key is in the POST response only.
 *
 * Owner/admin only (server 403 otherwise). Wire is snake_case (CLAUDE.md §3).
 */

import { ApiError, type HttpClient } from './http-client.js';

export type UcpReason =
  | 'site_not_found'
  | 'stripe_not_connected'
  | 'stripe_publishable_key_missing'
  | 'stripe_rail_disabled'
  | 'settlement_rail_not_stripe'
  | 'policy_inactive'
  | 'google_pay_merchant_id_missing';

export interface UcpGooglePay {
  readonly merchant_id: string | null;
  readonly merchant_name: string | null;
}

export interface UcpShippingOption {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly amount_minor: number;
  readonly countries?: readonly string[];
  readonly free_over_minor?: number;
}
export interface UcpTaxRule {
  readonly country: string;
  readonly region?: string;
  readonly rate_bp: number;
  readonly tax_shipping?: boolean;
}
export interface UcpFulfillment {
  readonly shipping_options: readonly UcpShippingOption[];
  readonly tax_rules: readonly UcpTaxRule[];
}

/** ADR-0254 — which of the merchant's Stripe promotion codes agents may use. */
export type UcpDiscountMode = 'off' | 'all' | 'allow_list';
export interface UcpDiscountSetting {
  readonly mode: UcpDiscountMode;
  /** Upper-cased codes; used when `mode === 'allow_list'`. */
  readonly allow_list: readonly string[];
}

export interface UcpPlatformKey {
  readonly configured: boolean;
  /** Last four characters of the key; never the key itself. */
  readonly hint: string | null;
  readonly created_at: string | null;
  /** Platform profile hosts the key is accepted for (e.g. `ucp.goog`). */
  readonly platform_hosts: readonly string[];
}

export interface UcpReadiness {
  readonly checkout: boolean;
  readonly reasons: readonly UcpReason[];
  readonly capabilities: readonly string[];
  readonly environment: 'TEST' | 'PRODUCTION';
  readonly profile_url: string;
  readonly endpoint: string;
  readonly google_pay: UcpGooglePay;
  readonly fulfillment: UcpFulfillment;
  readonly discounts: UcpDiscountSetting;
  readonly platform_key: UcpPlatformKey;
}

export type GetSiteUcpResult =
  | { readonly kind: 'ok'; readonly readiness: UcpReadiness }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type PutSiteUcpResult =
  | { readonly kind: 'ok'; readonly checkout: boolean; readonly reasons: readonly UcpReason[]; readonly capabilities: readonly string[]; readonly google_pay: UcpGooglePay; readonly fulfillment: UcpFulfillment; readonly discounts: UcpDiscountSetting }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'bad_request'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

const REASONS: ReadonlySet<string> = new Set(['site_not_found', 'stripe_not_connected', 'stripe_publishable_key_missing', 'stripe_rail_disabled', 'settlement_rail_not_stripe', 'policy_inactive', 'google_pay_merchant_id_missing']);

function parseReasons(raw: unknown): UcpReason[] {
  return Array.isArray(raw) ? raw.filter((r): r is UcpReason => typeof r === 'string' && REASONS.has(r)) : [];
}
function parseFulfillment(raw: unknown): UcpFulfillment {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const opts = Array.isArray(o['shipping_options']) ? (o['shipping_options'] as Record<string, unknown>[]) : [];
  const rules = Array.isArray(o['tax_rules']) ? (o['tax_rules'] as Record<string, unknown>[]) : [];
  return {
    shipping_options: opts.filter((x) => typeof x['id'] === 'string' && typeof x['title'] === 'string' && typeof x['amount_minor'] === 'number').map((x) => ({
      id: x['id'] as string, title: x['title'] as string, amount_minor: x['amount_minor'] as number,
      ...(typeof x['description'] === 'string' ? { description: x['description'] } : {}),
      ...(Array.isArray(x['countries']) ? { countries: (x['countries'] as unknown[]).filter((c): c is string => typeof c === 'string') } : {}),
      ...(typeof x['free_over_minor'] === 'number' ? { free_over_minor: x['free_over_minor'] } : {}),
    })),
    tax_rules: rules.filter((x) => typeof x['country'] === 'string' && typeof x['rate_bp'] === 'number').map((x) => ({
      country: x['country'] as string, rate_bp: x['rate_bp'] as number,
      ...(typeof x['region'] === 'string' ? { region: x['region'] } : {}),
      ...(x['tax_shipping'] === true ? { tax_shipping: true } : {}),
    })),
  };
}
function parseCapabilities(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string') : [];
}
function parseGooglePay(raw: unknown): UcpGooglePay {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    merchant_id: typeof o['merchant_id'] === 'string' ? o['merchant_id'] : null,
    merchant_name: typeof o['merchant_name'] === 'string' ? o['merchant_name'] : null,
  };
}

export function parseDiscountSetting(raw: unknown): UcpDiscountSetting {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const mode: UcpDiscountMode = o['mode'] === 'all' || o['mode'] === 'allow_list' ? o['mode'] : 'off';
  const allow_list = Array.isArray(o['allow_list']) ? (o['allow_list'] as unknown[]).filter((c): c is string => typeof c === 'string') : [];
  return { mode, allow_list };
}

export function parsePlatformKey(raw: unknown): UcpPlatformKey {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    configured: o['configured'] === true,
    hint: typeof o['hint'] === 'string' ? o['hint'] : null,
    created_at: typeof o['created_at'] === 'string' ? o['created_at'] : null,
    platform_hosts: Array.isArray(o['platform_hosts']) ? (o['platform_hosts'] as unknown[]).filter((h): h is string => typeof h === 'string') : [],
  };
}

export async function getSiteUcp(http: HttpClient, site_id: string): Promise<GetSiteUcpResult> {
  try {
    const res = await http.request<Record<string, unknown>>({ method: 'GET', path: `/v1/sites/${encodeURIComponent(site_id)}/ucp` });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return {
      kind: 'ok',
      readiness: {
        checkout: res['checkout'] === true,
        reasons: parseReasons(res['reasons']),
        capabilities: parseCapabilities(res['capabilities']),
        environment: res['environment'] === 'PRODUCTION' ? 'PRODUCTION' : 'TEST',
        profile_url: typeof res['profile_url'] === 'string' ? res['profile_url'] : '',
        endpoint: typeof res['endpoint'] === 'string' ? res['endpoint'] : '',
        google_pay: parseGooglePay(res['google_pay']),
        fulfillment: parseFulfillment(res['fulfillment']),
        discounts: parseDiscountSetting(res['discounts']),
        platform_key: parsePlatformKey(res['platform_key']),
      },
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: err.message };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

export async function putSiteUcp(http: HttpClient, site_id: string, patch: { google_pay?: UcpGooglePay; fulfillment?: UcpFulfillment; discounts?: UcpDiscountSetting }): Promise<PutSiteUcpResult> {
  try {
    const res = await http.request<Record<string, unknown>>({ method: 'PUT', path: `/v1/sites/${encodeURIComponent(site_id)}/ucp`, body: patch });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', checkout: res['checkout'] === true, reasons: parseReasons(res['reasons']), capabilities: parseCapabilities(res['capabilities']), google_pay: parseGooglePay(res['google_pay']), fulfillment: parseFulfillment(res['fulfillment']), discounts: parseDiscountSetting(res['discounts']) };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') return { kind: 'bad_request', reason: err.server_reason ?? 'bad_request' };
      return { kind: 'error', message: err.message };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

export type CreatePlatformKeyResult =
  | { readonly kind: 'ok'; readonly key: string; readonly platform_key: UcpPlatformKey }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type RevokePlatformKeyResult =
  | { readonly kind: 'ok'; readonly platform_key: UcpPlatformKey }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

function failure(err: unknown): { kind: 'forbidden' } | { kind: 'not_found' } | { kind: 'error'; message: string } {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    if (err.kind === 'not_found') return { kind: 'not_found' };
    return { kind: 'error', message: err.message };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
}

/** Mint (or replace) the site's platform access key. The key is returned once. */
export async function createSitePlatformKey(http: HttpClient, site_id: string): Promise<CreatePlatformKeyResult> {
  try {
    const res = await http.request<Record<string, unknown>>({ method: 'POST', path: `/v1/sites/${encodeURIComponent(site_id)}/ucp/platform-key` });
    if (res === undefined || typeof res['key'] !== 'string') return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', key: res['key'], platform_key: parsePlatformKey(res['platform_key']) };
  } catch (err) {
    return failure(err);
  }
}

export async function revokeSitePlatformKey(http: HttpClient, site_id: string): Promise<RevokePlatformKeyResult> {
  try {
    const res = await http.request<Record<string, unknown>>({ method: 'DELETE', path: `/v1/sites/${encodeURIComponent(site_id)}/ucp/platform-key` });
    return { kind: 'ok', platform_key: parsePlatformKey(res?.['platform_key']) };
  } catch (err) {
    return failure(err);
  }
}
